import React, { useState, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Canvas } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Stage,
  Environment,
  Center,
  Loader,
} from "@react-three/drei";
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  AlertCircle, 
  Check, 
  Building2,
  Briefcase,
  Users,
  Handshake,
  GraduationCap,
  BookOpen,
  HeartHandshake,
  Brain,
  School,
  Accessibility
} from 'lucide-react';
import emailjs from '@emailjs/browser';

// ✅ Configuration EmailJS
const EMAILJS_CONFIG = {
  serviceId: import.meta.env.VITE_EMAILJS_SERVICE_ID,
  contactTemplateId: import.meta.env.VITE_EMAILJS_ADMIN_TEMPLATE_ID,
  publicKey: import.meta.env.VITE_EMAILJS_PUBLIC_KEY
};

// Composant Model pour l'icône 3D
function Model() {
  const { scene } = useGLTF("/models/email_icon.glb");
  return (
    <Center>
      <primitive
        object={scene}
        scale={[0.015, 0.015, 0.015]}
        position={[0, -1, 0]}
        rotation={[0, Math.PI / 4, 0]}
      />
    </Center>
  );
}

// Préchargement du modèle
useGLTF.preload("/models/email_icon.glb");

// Composant de la scène 3D
function ContactScene() {
  return (
    <Canvas
      shadows
      camera={{ position: [6, 3, 6], fov: 45 }}
      className="fixed inset-0"
    >
      <color attach="background" args={["#121F45"]} />
      <Suspense fallback={null}>
        <Stage environment="city">
          <Model />
        </Stage>
        <Environment preset="city" />
        <OrbitControls autoRotate />
      </Suspense>
    </Canvas>
  );
}

const ContactFeatures = ({ activeTab }) => {
  const featuresMap = {
    'admin': [
      {
        icon: <BookOpen className="w-6 h-6 text-purple-400" />,
        title: "Support du Site web",
        description: "Vous voulez suggérer des améliorations, vous rencontrez des problèmes ? N'hésitez pas à nous contacter !"
      },
      {
        icon: <GraduationCap className="w-6 h-6 text-green-400" />,
        title: "Suivi Pédagogique",
        description: "Information sur les formations et le suivi des apprentis"
      },
      {
        icon: <Users className="w-6 h-6 text-blue-400" />,
        title: "Accompagnement",
        description: "Aide personnalisée pour votre parcours au CFA"
      }
    ],
    'partner': [
      {
        icon: <Handshake className="w-6 h-6 text-purple-400" />,
        title: "Partenariat",
        description: "Développez votre réseau avec notre CFA"
      },
      {
        icon: <Building2 className="w-6 h-6 text-green-400" />,
        title: "Entreprises",
        description: "Collaborez avec nos formations professionnelles"
      },
      {
        icon: <Briefcase className="w-6 h-6 text-blue-400" />,
        title: "Opportunités",
        description: "Accédez à un vivier de talents en formation"
      }
    ],
    'dys': [
      {
        icon: <Brain className="w-6 h-6 text-yellow-400" />,
        title: "Accompagnement Handicap",
        description: "Soutien spécifique pour les apprenants ayant des troubles DYS"
      },
      {
        icon: <School className="w-6 h-6 text-purple-400" />,
        title: "Adaptation Pédagogique",
        description: "Méthodes adaptées aux besoins spécifiques des apprenants"
      },
      {
        icon: <HeartHandshake className="w-6 h-6 text-green-400" />,
        title: "Suivi Personnalisé",
        description: "Accompagnement individualisé pour la réussite de chaque apprenant"
      }
    ]
  };

  const features = featuresMap[activeTab] || featuresMap['admin'];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 }}
      className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
    >
      {features.map((feature, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          whileHover={{ scale: 1.02 }}
          className={`bg-slate-800/60 backdrop-blur-sm rounded-lg p-6 hover:bg-slate-700/60 transition-colors cursor-pointer border-l-4 ${
            activeTab === 'dys' 
              ? 'border-yellow-500 hover:border-yellow-400' 
              : activeTab === 'partner'
                ? 'border-green-500 hover:border-green-400'
                : 'border-blue-500 hover:border-pink-500'
          }`}
        >
          <div className="flex items-start space-x-4">
            <motion.div 
              className={`p-3 ${
                activeTab === 'dys' 
                  ? 'bg-yellow-800/30' 
                  : activeTab === 'partner'
                    ? 'bg-green-800/30'
                    : 'bg-slate-700/50'
              } rounded-lg`}
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.5 }}
            >
              <div className={
                activeTab === 'dys' 
                  ? "text-yellow-400" 
                  : activeTab === 'partner'
                    ? "text-green-400"
                    : "text-blue-400"
              }>
                {feature.icon}
              </div>
            </motion.div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-300 text-sm">
                {feature.description}
              </p>
            </div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

const Contact = () => {
  const [activeTab, setActiveTab] = useState('admin');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    company: '',
    sector: '',
    partnershipType: '',
    studentName: '',
    studentClass: '',
    dysType: ''
  });

  const [status, setStatus] = useState({
    type: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setStatus({ type: '', message: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.message) {
      setStatus({
        type: 'error',
        message: 'Veuillez remplir tous les champs obligatoires'
      });
      return;
    }

    try {
      let toEmail;
      let emailTitle;
      let emailIntro;
      
      // ✅ Définir le destinataire et le contenu selon l'onglet
      if (activeTab === 'admin') {
        toEmail = 'aubry.jean-charles@cfa-montagis.com';
        emailTitle = '🔧 Nouveau message';
        emailIntro = 'Un nouveau message a été reçu depuis le formulaire de contact.';
      } else if (activeTab === 'partner') {
        toEmail = 'sciauvaud.julien@cfa-montargis.com';
        emailTitle = '🤝 Demande de Partenariat';
        emailIntro = 'Une nouvelle demande de partenariat a été reçue.';
      } else if (activeTab === 'dys') {
        toEmail = 'handicap@cfa-montargis.com';
        emailTitle = '♿ Demande d\'Accompagnement Handicap';
        emailIntro = 'Une nouvelle demande concernant l\'accompagnement handicap a été reçue.';
      }
      
      // ✅ Paramètres pour l'email
      const templateParams = {
        to_email: toEmail,
        from_name: formData.name,
        from_email: formData.email,
        message: formData.message,
        phone: formData.phone || '',
        email_title: emailTitle,
        email_intro: emailIntro,
        company: formData.company || '',
        sector: formData.sector || '',
        partnershipType: formData.partnershipType || '',
        studentName: formData.studentName || '',
        studentClass: formData.studentClass || '',
        dysType: formData.dysType || ''
      };

      console.log('📧 Envoi de l\'email...');
      console.log('   Destinataire:', toEmail);
      console.log('   Template:', EMAILJS_CONFIG.contactTemplateId);

      // ✅ Envoi direct avec EmailJS
      await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.contactTemplateId,
        templateParams,
        EMAILJS_CONFIG.publicKey
      );

      console.log('✅ Email envoyé avec succès');

      setStatus({
        type: 'success',
        message: 'Votre message a été envoyé avec succès !'
      });

      // Réinitialiser le formulaire
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        company: '',
        sector: '',
        partnershipType: '',
        studentName: '',
        studentClass: '',
        dysType: ''
      });
    } catch (error) {
      console.error('❌ Erreur lors de l\'envoi:', error);
      setStatus({
        type: 'error',
        message: 'Une erreur est survenue lors de l\'envoi du message.'
      });
    }
  };

  const getContactEmail = () => {
    if (activeTab === 'admin') return 'aubry.jean-charles@cfa-montagis.com';
    if (activeTab === 'partner') return 'sciauvaud.julien@cfa-montargis.com';
    if (activeTab === 'dys') return 'handicap@cfa-montargis.com';
    return 'contact@cfa-montargis.com';
  };
  
  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6 text-pink-400" />,
      title: 'Téléphone',
      content: '+33 2 38 98 05 92',
      link: 'tel:+33238980592'
    },
    {
      icon: <Mail className="w-6 h-6 text-yellow-400" />,
      title: 'Email',
      content: getContactEmail(),
      link: `mailto:${getContactEmail()}`
    },
    {
      icon: <MapPin className="w-6 h-6 text-red-400" />,
      title: 'Adresse',
      content: '3 rue Cyrille Robert, 45200 Montargis',
      link: 'https://maps.google.com'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen py-40"
    >
      <div className="fixed inset-0 z-0">
        <ContactScene />
      </div>

      <Loader />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.h1
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-6xl font-bold text-center mb-8 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500"
          >
            Contactez-nous
          </motion.h1>
          
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="text-xl text-center text-white font-medium max-w-2xl mx-auto mb-12 bg-slate-800/60 p-6 rounded-lg backdrop-blur-sm"
          >
            Notre équipe est à votre disposition pour répondre à toutes vos questions
          </motion.p>
        </motion.div>

        <div className="flex justify-center mb-12">
          <motion.div
            className="bg-slate-800/60 backdrop-blur-sm rounded-lg p-1 inline-flex flex-wrap justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <button
              onClick={() => setActiveTab('admin')}
              className={`px-6 py-3 rounded-lg transition-colors m-1 ${
                activeTab === 'admin'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Contact Administrateur
            </button>
            <button
              onClick={() => setActiveTab('partner')}
              className={`px-6 py-3 rounded-lg transition-colors m-1 ${
                activeTab === 'partner'
                  ? 'bg-green-600 text-white'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Devenir Partenaire
            </button>
            <button
              onClick={() => setActiveTab('dys')}
              className={`px-6 py-3 rounded-lg transition-colors m-1 ${
                activeTab === 'dys'
                  ? 'bg-yellow-600 text-white'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-2">
                <Accessibility className="w-4 h-4" />
                Accompagnement Handicap
              </div>
            </button>
          </motion.div>
        </div>

        <ContactFeatures activeTab={activeTab} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="space-y-6">
            {contactInfo.map((info, index) => (
              <motion.a
                key={index}
                href={info.link}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ y: 20, opacity: 0 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`block ${
                  activeTab === 'dys' 
                    ? 'bg-yellow-900/20' 
                    : activeTab === 'partner'
                      ? 'bg-green-900/20'
                      : 'bg-slate-800/60'
                } backdrop-blur-sm p-6 rounded-lg hover:bg-slate-700/60 transition-colors`}
              >
                <div className="flex items-center space-x-4">
                  <motion.div 
                    className={
                      activeTab === 'dys' 
                        ? "text-yellow-400" 
                        : activeTab === 'partner'
                          ? "text-green-400"
                          : "text-blue-400"
                    }
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                  >
                    {info.icon}
                  </motion.div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {info.title}
                    </h3>
                    <p className="text-gray-300">
                      {info.content}
                    </p>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className={`lg:col-span-2 ${
              activeTab === 'dys' 
                ? 'bg-yellow-900/20' 
                : activeTab === 'partner'
                  ? 'bg-green-900/20'
                  : 'bg-slate-800/60'
            } backdrop-blur-sm p-8 rounded-lg`}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full ${
                      activeTab === 'dys' 
                        ? 'bg-yellow-900/30' 
                        : activeTab === 'partner'
                          ? 'bg-green-900/30'
                          : 'bg-slate-700/50'
                    } border border-slate-600 rounded-lg py-2 px-4 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors`}
                    placeholder="John Doe"
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full ${
                      activeTab === 'dys' 
                        ? 'bg-yellow-900/30' 
                        : activeTab === 'partner'
                          ? 'bg-green-900/30'
                          : 'bg-slate-700/50'
                    } border border-slate-600 rounded-lg py-2 px-4 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors`}
                    placeholder="john@example.com"
                  />
                </motion.div>
              </div>

              {activeTab === 'partner' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Entreprise *
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full bg-green-900/30 border border-slate-600 rounded-lg py-2 px-4 text-white placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors"
                      placeholder="Nom de l'entreprise"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Secteur d'activité *
                    </label>
                    <input
                      type="text"
                      name="sector"
                      value={formData.sector}
                      onChange={handleChange}
                      className="w-full bg-green-900/30 border border-slate-600 rounded-lg py-2 px-4 text-white placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors"
                      placeholder="Ex: Automobile, Transport..."
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Type de partenariat souhaité *
                    </label>
                    <select
                      name="partnershipType"
                      value={formData.partnershipType}
                      onChange={handleChange}
                      className="w-full bg-green-900/30 border border-slate-600 rounded-lg py-2 px-4 text-white placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors"
                    >
                      <option value="">Sélectionnez un type</option>
                      <option value="formation">Formation</option>
                      <option value="stage">Stage</option>
                      <option value="apprentissage">Apprentissage</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                </motion.div>
              )}
              
              {activeTab === 'dys' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Nom de l'élève (si différent)
                    </label>
                    <input
                      type="text"
                      name="studentName"
                      value={formData.studentName}
                      onChange={handleChange}
                      className="w-full bg-yellow-900/30 border border-slate-600 rounded-lg py-2 px-4 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors"
                      placeholder="Nom de l'élève concerné"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Classe/Formation
                    </label>
                    <input
                      type="text"
                      name="studentClass"
                      value={formData.studentClass}
                      onChange={handleChange}
                      className="w-full bg-yellow-900/30 border border-slate-600 rounded-lg py-2 px-4 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors"
                      placeholder="Ex: CAP Coiffure, Bac Pro..."
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Type de trouble DYS
                    </label>
                    <select
                      name="dysType"
                      value={formData.dysType}
                      onChange={handleChange}
                      className="w-full bg-yellow-900/30 border border-slate-600 rounded-lg py-2 px-4 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors"
                    >
                      <option value="">Sélectionnez si applicable</option>
                      <option value="dyslexie">Dyslexie</option>
                      <option value="dyspraxie">Dyspraxie</option>
                      <option value="dysorthographie">Dysorthographie</option>
                      <option value="dyscalculie">Dyscalculie</option>
                      <option value="dysphasie">Dysphasie</option>
                      <option value="tdah">TDAH</option>
                      <option value="autre">Autre</option>
                      <option value="non_specifie">Non spécifié/Je ne sais pas</option>
                    </select>
                  </div>
                </motion.div>
              )}
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full ${
                    activeTab === 'dys' 
                      ? 'bg-yellow-900/30' 
                      : activeTab === 'partner'
                        ? 'bg-green-900/30'
                        : 'bg-slate-700/50'
                  } border border-slate-600 rounded-lg py-2 px-4 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors`}
                  placeholder="+33 2 38 98 05 92"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="6"
                  className={`w-full ${
                    activeTab === 'dys' 
                      ? 'bg-yellow-900/30' 
                      : activeTab === 'partner'
                        ? 'bg-green-900/30'
                        : 'bg-slate-700/50'
                  } border border-slate-600 rounded-lg py-2 px-4 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors resize-none`}
                  placeholder={activeTab === 'dys' 
                    ? "Décrivez les besoins spécifiques ou vos questions concernant l'accompagnement handicap ..." 
                    : "Votre message..."}
                />
              </motion.div>

              {status.message && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex items-center gap-2 p-4 rounded-lg ${
                    status.type === 'error'
                      ? 'bg-red-400/10 text-red-400'
                      : 'bg-green-400/10 text-green-400'
                  }`}
                >
                  {status.type === 'error' ? (
                    <AlertCircle className="w-5 h-5" />
                  ) : (
                    <Check className="w-5 h-5" />
                  )}
                  <span>{status.message}</span>
                </motion.div>
              )}

              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full ${
                  activeTab === 'dys' 
                    ? 'bg-yellow-600 hover:bg-yellow-700' 
                    : activeTab === 'partner'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                } text-white py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 group`}
              >
                <Send className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                Envoyer le message
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default Contact;